import { auth } from "../firebase/firebase";

function isLoopbackOrLocalhostUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.hostname === "localhost" || u.hostname === "127.0.0.1";
  } catch {
    return true;
  }
}

/**
 * Base URL for the TrustForge Node API (Fastify).
 *
 * CRA `npm start` always uses same-origin so `src/setupProxy.js` can forward `/api`
 * even when the page is opened via a tunnel, LAN IP, or Cursor preview host.
 * Production builds with a loopback `REACT_APP_API_URL` also use same-origin on a
 * real hostname so nginx can proxy `/api` to the API container.
 */
export function getApiBaseUrl(): string {
  const explicitApi = (
    typeof process !== "undefined" ? process.env.REACT_APP_API_URL?.trim() ?? "" : ""
  ).replace(/\/$/, "");
  const hasRealApiHost = Boolean(explicitApi && !isLoopbackOrLocalhostUrl(explicitApi));
  if (hasRealApiHost) return explicitApi;

  const host =
    typeof window !== "undefined" && window.location?.hostname ? window.location.hostname : "";
  const isBrowserLocal = host === "localhost" || host === "127.0.0.1";
  const isDev = typeof process !== "undefined" && process.env.NODE_ENV === "development";

  if (typeof window !== "undefined" && (isDev || (host && !isBrowserLocal))) {
    return "";
  }

  if (explicitApi) return explicitApi;
  return "http://127.0.0.1:8001";
}

const API_BASE = getApiBaseUrl();

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

const withQuery = (path: string, query?: Record<string, string | number | boolean | undefined>) => {
  if (!query) return path;
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    params.set(key, String(value));
  });
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
};

export async function apiRequest<T>(
  method: HttpMethod,
  path: string,
  options?: {
    query?: Record<string, string | number | boolean | undefined>;
    body?: unknown;
  },
): Promise<T> {
  const user = auth.currentUser;
  const token = user ? await user.getIdToken() : null;
  const headers: Record<string, string> = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  if (options?.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  const res = await fetch(`${API_BASE}${withQuery(path, options?.query)}`, {
    method,
    headers,
    body: options?.body === undefined ? undefined : JSON.stringify(options.body),
  });

  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get("content-type") || "";
  if (path.startsWith("/api") && contentType.includes("text/html")) {
    const url = `${API_BASE}${withQuery(path, options?.query)}`;
    throw new Error(
      `The app received HTML instead of JSON from ${url}. Restart the UI after pulling (CRA proxies /api to :8001). In Docker, nginx must forward /api to the API container.`,
    );
  }

  const payload = await res.json().catch(() => ({} as Record<string, unknown>));
  if (!res.ok) {
    const body =
      typeof payload === "object" && payload
        ? (payload as { message?: string; error?: string; code?: string })
        : {};
    const message = body.message || body.error || `Request failed (${res.status})`;
    throw new Error(message);
  }
  return payload as T;
}
